import { useFormik } from 'formik'
import { useMemo } from 'react'
import { toast } from 'react-toastify'
import * as yup from 'yup'

import { getAgentChatSettings } from '~/features/agents/utils'
import useGetLatestFormSubmission from '~/features/chatbotV2/hooks/useGetLatestFormSubmission'
import {
  buildPreChatFormValidationSchema,
  createEmptyPreChatFormFieldValues,
  getPreChatFormInitialValues,
  mapPreChatFormValuesToSubmissionIds,
} from '~/features/chatbotV2/hooks/usePreChatForm/utils'
import useSubmitPreChatForm from '~/features/chatbotV2/hooks/useSubmitPreChatForm'
import {
  hasPreChatFormSubmittedForConversation,
  shouldShowPreChatForm,
} from '~/features/chatbotV2/utils'
import {
  EPreChatFormKeys,
  TGuestPreChatFormField,
} from '~/features/preChatForms/models'
import { sortPreChatFormFields } from '~/features/preChatForms/utils'
import { store } from '~/store'

type TParams = {
  guestToken: string | null
  isSessionReady: boolean
  refreshGuestToken?: () => Promise<string | null>
}

const usePreChatForm = ({
  guestToken,
  isSessionReady,
  refreshGuestToken,
}: TParams) => {
  const chatbot = store((state) => state.chatbot)
  const { conversationId, agent: chatbotAgent } = chatbot
  const form = getAgentChatSettings(chatbotAgent.chatSettings)?.form
  const agentId = chatbotAgent.id ?? ''

  const {
    mutateAsync: submitPreChatForm,
    isPending: isSubmittingPreChatForm,
    isSuccess: hasSubmittedPreChatForm,
  } = useSubmitPreChatForm(conversationId)
  const {
    data: latestFormSubmission,
    isFetched: isLatestFormSubmissionFetched,
    isError: isLatestFormSubmissionError,
  } = useGetLatestFormSubmission({
    guestToken,
    refreshGuestToken,
    queryOptions: {
      enabled: !!form,
    },
  })

  const canResolvePreChatVisibility = !!guestToken && isSessionReady && !!form

  const isPriorSubmissionQuerySettled =
    isLatestFormSubmissionFetched || isLatestFormSubmissionError

  const priorSubmission = latestFormSubmission?.priorSubmission ?? null

  const shouldShow = useMemo(() => {
    if (!canResolvePreChatVisibility || !isPriorSubmissionQuerySettled) {
      return false
    }

    if (isLatestFormSubmissionError) {
      return false
    }

    return shouldShowPreChatForm({
      form,
      isPriorSubmissionResolved: true,
      priorSubmission,
    })
  }, [
    canResolvePreChatVisibility,
    form,
    isLatestFormSubmissionError,
    isPriorSubmissionQuerySettled,
    priorSubmission,
  ])
  const hasSubmittedForConversation = useMemo(
    () =>
      !!conversationId &&
      (hasSubmittedPreChatForm ||
        hasPreChatFormSubmittedForConversation(agentId, conversationId)),
    [agentId, conversationId, hasSubmittedPreChatForm],
  )

  const isPreChatFormVisible = shouldShow && !hasSubmittedForConversation

  const sortedFields = useMemo<TGuestPreChatFormField[]>(
    () => (form ? sortPreChatFormFields(form[EPreChatFormKeys.fields]) : []),
    [form],
  )

  const initialValues = useMemo(
    () =>
      form
        ? getPreChatFormInitialValues(form, priorSubmission)
        : createEmptyPreChatFormFieldValues(),
    [form, priorSubmission],
  )

  const validationSchema = useMemo(
    () =>
      sortedFields.length > 0
        ? buildPreChatFormValidationSchema(sortedFields)
        : yup.object({}),
    [sortedFields],
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      if (!guestToken || !form) {
        toast.error('Could not submit the form. Please try again.')
        throw new Error('pre-chat-form-submit-unavailable')
      }

      if (!conversationId) {
        toast.error(
          'Chat is not ready yet. Please wait a moment and try again.',
        )
        throw new Error('pre-chat-form-submit-unavailable')
      }

      const submissionValues = mapPreChatFormValuesToSubmissionIds(
        sortedFields,
        values,
      )

      try {
        await submitPreChatForm({
          agentId,
          conversationId,
          guestToken,
          refreshGuestToken,
          formId: form[EPreChatFormKeys.id],
          values: submissionValues,
        })
      } catch {
        toast.error('Could not submit the form. Please try again.')
        throw new Error('pre-chat-form-submit-failed')
      }
    },
  })

  const { isValid } = formik

  const isPreChatFormSendDisabled =
    isPreChatFormVisible && (!isValid || isSubmittingPreChatForm)

  return {
    formik,
    isPreChatFormSendDisabled,
    isPreChatFormVisible,
    isSubmittingPreChatForm,
    sortedFields,
  }
}

export default usePreChatForm
